import { api } from '../api';
import { logError } from '../../utils/logger';

export interface ProblemaClinico {
  id: string;
  label: string;
  codigo?: string;
  descricao?: string;
  categoria?: string;
  cid10?: string;
  dsm5?: string;
  ordem?: number;
}

export interface CatalogoData {
  queixas: string[];
  sintomas: string[];
  humor: string[];
  problemasClinicos: ProblemaClinico[];
}

export const catalogoService = {
  obterTodos: async (): Promise<CatalogoData> => {
    try {
      const response = await api.get<CatalogoData>('/catalogos');
      return response.data;
    } catch (error) {
      logError('CatalogoService', 'Erro ao buscar catálogos', error);
      throw new Error('Falha ao carregar catálogos');
    }
  },

  // CORREÇÃO: Usar rota singular como no backend
  async adicionarQueixa(valor: string, dados?: {
    descricao?: string;
    categoria?: string;
    ordem?: number;
  }): Promise<void> {
    await api.post('/catalogos/queixa', {
      valor,
      ...dados
    });
  },

  async adicionarSintoma(valor: string, dados?: {
    descricao?: string;
    categoria?: string;
    tipo_avaliacao?: 'intensidade' | 'frequencia' | 'ambos';
    ordem?: number;
  }): Promise<void> {
    await api.post('/catalogos/sintoma', {
      valor,
      ...dados
    });
  },

  async adicionarHumor(valor: string, dados?: {
    descricao?: string;
    categoria?: string;
    cor_indicativa?: string;
    ordem?: number;
  }): Promise<void> {
    await api.post('/catalogos/humor', {
      valor,
      ...dados
    });
  },

  async adicionarProblemaClinico(problema: {
    id: string;
    label: string;
    codigo?: string;
    descricao?: string;
    categoria?: string;
    cid10?: string;
    dsm5?: string;
    ordem?: number;
  }): Promise<void> {
    await api.post('/catalogos/problema-clinico', {
      codigo: problema.codigo || problema.id,
      valor: problema.label,
      descricao: problema.descricao,
      categoria: problema.categoria,
      cid10: problema.cid10,
      dsm5: problema.dsm5,
      ordem: problema.ordem
    });
  },

  removerQueixa: async (valor: string): Promise<string[]> => {
    try {
      const response = await api.delete<{queixas: string[]}>(`/catalogos/queixa/${encodeURIComponent(valor)}`);
      return response.data.queixas || [];
    } catch (error) {
      logError('CatalogoService', 'Erro ao remover queixa', error);
      throw error;
    }
  },

  removerSintoma: async (valor: string): Promise<string[]> => {
    try {
      const response = await api.delete<{sintomas: string[]}>(`/catalogos/sintoma/${encodeURIComponent(valor)}`);
      return response.data.sintomas || [];
    } catch (error) {
      logError('CatalogoService', 'Erro ao remover sintoma', error);
      throw error;
    }
  },

  removerHumor: async (valor: string): Promise<string[]> => {
    try {
      const response = await api.delete<{humor: string[]}>(`/catalogos/humor/${encodeURIComponent(valor)}`);
      return response.data.humor || [];
    } catch (error) {
      logError('CatalogoService', 'Erro ao remover humor', error);
      throw error;
    }
  },

  removerProblemaClinico: async (id: string): Promise<ProblemaClinico[]> => {
    try {
      const response = await api.delete<{problemasClinicos: ProblemaClinico[]}>(`/catalogos/problema-clinico/${encodeURIComponent(id)}`);
      return response.data.problemasClinicos || [];
    } catch (error) {
      logError('CatalogoService', 'Erro ao remover problema clínico', error);
      throw error;
    }
  },

  atualizarProblemaClinico: async (
    codigo: string, 
    problema: {
      codigo: string;
      valor: string;
      descricao?: string;
      categoria?: string;
      cid10?: string;
      dsm5?: string;
      ordem?: number;
    }
  ): Promise<{ sucesso: boolean; mensagem: string; problemasClinicos?: ProblemaClinico[] }> => {
    try {
      const response = await api.put<{
        mensagem: string;
        problemasClinicos: ProblemaClinico[];
      }>(`/catalogos/problema-clinico/${encodeURIComponent(codigo)}`, {
        valor: problema.valor,
        descricao: problema.descricao || '',
        categoria: problema.categoria || '',
        cid10: problema.cid10 || '',
        dsm5: problema.dsm5 || '',
        ordem: problema.ordem || 0
      });
      
      return {
        sucesso: true,
        mensagem: response.data.mensagem,
        problemasClinicos: response.data.problemasClinicos
      };
    } catch (error) {
      logError('CatalogoService', 'Erro ao atualizar problema clínico', error);
      return {
        sucesso: false,
        mensagem: 'Erro ao atualizar problema clínico'
      };
    }
  },

  obterProblemasClinicos: async (): Promise<ProblemaClinico[]> => {
    try {
      const response = await api.get<ProblemaClinico[]>('/catalogos/problemas-clinicos');
      return response.data;
    } catch (error) {
      logError('CatalogoService', 'Erro ao obter problemas clínicos', error);
      throw new Error('Falha ao carregar problemas clínicos');
    }
  }
};
